import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@datanomik/core-ui';

import { ThemeModeToggle } from '@/modern/theme/ThemeModeToggle';

import { HackathonBanner } from './HackathonBanner';
import { DatanomikMark } from './DatanomikMark';
import loginBackground from '../assets/login-bg.webp';

interface AuthLayoutProps {
  children: ReactNode;
}

const PANEL_BACKGROUND = {
  backgroundImage: `url(${loginBackground})`,
  backgroundPosition: '42% bottom',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
};

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation();
  return (
    <div data-modern className="flex min-h-screen bg-background font-sans">
      <aside
        className="relative hidden box-border w-1/2 flex-col justify-between overflow-hidden bg-brand-deep p-10 lg:flex"
        style={PANEL_BACKGROUND}
      >
        <DatanomikMark className="relative size-7 text-brand-foreground" />

        <div className="relative flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <Text variant="title" as="h2" className="text-brand-foreground">
              {t('Welcome to Datanomik')}
            </Text>
            <Text variant="body" className="max-w-sm text-brand-foreground/75">
              {t("Understand your company's cash flow and make strategic decisions.")}
            </Text>
          </div>

          <HackathonBanner />
        </div>

        <Text variant="caption" className="relative text-brand-foreground/50">
          {t('© {{year}} Datanomik. All rights reserved.', { year: new Date().getFullYear() })}
        </Text>
      </aside>

      <main className="flex flex-1 flex-col">
        <div className="flex justify-end p-4">
          <ThemeModeToggle />
        </div>
        <div className="flex flex-1 items-center justify-center px-4 pb-16">
          <div className="w-full max-w-sm">{children}</div>
        </div>
      </main>
    </div>
  );
}
